import { FUNCTIONS, type Node } from './parse';

/**
 * Evaluating a parsed expression at a value of `x`.
 *
 * ## `null` is the answer, not an exception
 *
 * `1/0`, `sqrt(-1)`, `ln(-3)`, `tan(π/2)` — a school function is undefined at plenty of
 * points, and the grapher meets them by the thousand while sampling. Returning `null` says
 * "there is no value here", which is the same stance the geometry kernel takes for a
 * degenerate construction, and it lets the sampler break the curve instead of drawing a line
 * to infinity.
 *
 * Throwing would be wrong twice over: it costs a stack unwind on a hot loop, and it turns an
 * ordinary property of the function into an error condition.
 *
 * ## No allocation in the loop
 *
 * This runs once per sample and a graph has hundreds. The evaluator walks the tree directly
 * rather than compiling to a closure — a closure tree is faster in a micro-benchmark and
 * builds a few hundred objects per re-parse, which is the wrong trade when the expression
 * changes on every keystroke.
 */
export function evaluateAt(node: Node, x: number): number | null {
  const value = walk(node, x);
  // One check at the top rather than after every operation: infinities and NaN propagate
  // through arithmetic, so a single test at the end catches everything below.
  return Number.isFinite(value) ? value : null;
}

function walk(node: Node, x: number): number {
  switch (node.kind) {
    case 'number':
      return node.value;

    case 'variable':
      return x;

    case 'unary':
      return -walk(node.operand, x);

    case 'binary': {
      const left = walk(node.left, x);
      const right = walk(node.right, x);

      switch (node.operator) {
        case '+':
          return left + right;
        case '-':
          return left - right;
        case '*':
          return left * right;
        case '/':
          return left / right;
        case '^':
          /*
           * `(-8)^(1/3)` is `NaN` in IEEE arithmetic, and on a blackboard it is −2. The odd
           * integer root is the one case where a school reader and the floating-point
           * specification disagree, and following the specification would leave a hole in the
           * graph of the cube root exactly where the interesting part is.
           *
           * Only *odd* roots are rescued. `(-4)^(1/2)` genuinely has no real value and stays
           * undefined, which is the honest answer.
           */
          if (left < 0 && !Number.isInteger(right)) {
            const inverse = 1 / right;
            if (Number.isInteger(inverse) && Math.abs(inverse) % 2 === 1) {
              return -Math.pow(-left, right);
            }
          }
          return Math.pow(left, right);
      }
      break;
    }

    case 'call': {
      const args = node.args.map((argument) => walk(argument, x));
      return FUNCTIONS[node.name].apply(args);
    }
  }

  return Number.NaN;
}

/**
 * Whether an expression's value depends on `x` at all.
 *
 * A constant expression is a horizontal line, which is fine — but it also means the sampler
 * can stop looking for discontinuities, and it lets the interface say "this is a constant"
 * rather than leaving the user wondering why their curve is flat.
 */
export function dependsOnVariable(node: Node): boolean {
  switch (node.kind) {
    case 'variable':
      return true;
    case 'number':
      return false;
    case 'unary':
      return dependsOnVariable(node.operand);
    case 'binary':
      return dependsOnVariable(node.left) || dependsOnVariable(node.right);
    case 'call':
      return node.args.some(dependsOnVariable);
  }
}
